import { ApiService } from './api.service';
import { EvalSettingSimple, UpdateEvalSettingSimpleRequest } from '@/models/evalSetting/eval-setting-simple';
import { PagingData } from '@/models/core/paging-data';
import { EvalSetting, EvalSettingDetailRequest, ImportRequestResults, UpdateExamineeStudentsResult, GrowCourseScoreUpdateStatus } from '@/models/evalSetting/eval-setting';
import { Empty } from '@/models/core/empty';
import { Student } from '@/models/student/student';
import { ReportStatus } from '@/models/evalSetting/report-status';
import { ExamineeStudentProgress } from '@/models/evalSetting/evaluator-student-progress';
import { ReportZipUrl } from '@/models/evalSetting/report-zip-url';
import { GEnum } from '@/constants/enum.defination';
import { AddEvalRequest } from '@/models/evalSetting/add-eval-request';
import { DeleteEvalRequest } from '@/models/evalSetting/delete-eval-request';
import { EvaluatorCandidateStudent, EvaluatorCandidateStudentRequest } from '@/models/evalSetting/evaluator-candidate-student';
import { AllEvalSetting } from '@/models/evalSetting/all-eval-setting';
import { EvalSettingMailToggleResponse } from '@/models/evalSetting/eval-setting-mail';

class EvalSettingService extends ApiService {
  constructor() {
    super();
  }

  /**
   * Get evaluatee summary list
   */
  getEvalSettingsForAcademic(limit: number = 15, offset: number = 0, hasStatusCompleted: boolean): Promise<PagingData<EvalSettingSimple>> {
    return this.getPagingData(EvalSettingSimple, `evalSettings`, {
      offset,
      limit,
      hasStatusCompleted,
    });
  }

  /**
   * 全受検設定取得
   */
  getAllEvalSettings(): Promise<AllEvalSetting[]> {
    return this.getList(AllEvalSetting, `/allEvalSettings`, {});
  }

  /**
   * 進捗ダウンロード
   * @param evalSettingID
   */
  downloadProgress(evalSettingID: number): Promise<Blob> {
    return this.downloadFile(`/evalSettings/${evalSettingID}/progress`, {});
  }

  getEvalSettingDetail(evalSettingID: number): Promise<EvalSetting> {
    return this.get(EvalSetting, `/evalSettings/${evalSettingID}`, {});
  }

  /**
   * Get evaluatee students
   */
  getEvalSettingStudents(evalSettingID: number): Promise<Student[]> {
    return this.getList(Student, `/evalSettings/${evalSettingID}/students`, {});
  }

  /**
   * Add Eval setting detail
   */
  addEvalSettingForSchoolClasses(params: EvalSettingDetailRequest): Promise<EvalSetting> {
    return this.post(EvalSetting, `/evalSettings/schoolClasses`, params);
  }

  /**
   * Add Eval setting student
   */
  addEvalSettingForStudents(params: EvalSettingDetailRequest): Promise<EvalSetting> {
    return this.post(EvalSetting, `/evalSettings/students`, params);
  }

  /**
   * Update Eval setting
   */
  updateEvalSetting(evalSettingID: number, params: EvalSettingDetailRequest): Promise<EvalSetting> {
    return this.put(EvalSetting, `/evalSettings/${evalSettingID}`, params);
  }

  /**
   * Update Eval setting Simple
   */
  updateEvalSettingSimple(params: UpdateEvalSettingSimpleRequest): Promise<EvalSetting> {
    return this.put(EvalSetting, `/evalSettings/${params.evalSettingID}/simple`, {
      title: params.title,
      startTime: params.startTime,
      deadline: params.deadline,
    });
  }

  /**
   * get GrowCourseScoreUpdateStatus entity
   * @param giftCourseID
   * @returns
   */
  getUpdateStatusByGiftCourseID(giftCourseID: number): Promise<GrowCourseScoreUpdateStatus> {
    return this.get(GrowCourseScoreUpdateStatus, `/evalSettings/${giftCourseID}/scoreUpdateStatus`, {});
  }

  /**
   * update GrowCourseScoreUpdateStatus UpdateRequired
   * @param giftCourseID
   * @returns
   */
  updateOrInsertScoreUpdateStatus(giftCourseID: number, params: any): Promise<GrowCourseScoreUpdateStatus> {
    return this.put(GrowCourseScoreUpdateStatus, `/evalSettings/${giftCourseID}/scoreUpdateStatus`, params);
  }

  /**
   * Remove Eval setting detail
   * @param evalSettingID
   */
  deleteEvalSetting(evalSettingID: number): Promise<Empty> {
    return this.delete(`/evalSettings/${evalSettingID}`, {});
  }

  /**
   * Update Report Link
   */
  updateReportLink(params: UpdateEvalSettingSimpleRequest, reportType: number, reportEnabled: boolean): Promise<Empty> {
    return this.put(EvalSetting, `/evalSettings/${params.evalSettingID}/reportLink`, {
      title: params.title,
      startTime: params.startTime,
      deadline: params.deadline,
      PersonalReportDisplayType: reportType,
      IsPostingReportEnabled: reportEnabled,
    });
  }
  /**
   * 評価リクエスト用テンプレートファイル ダウンロード
   * @param evalSettingID
   */
  downloadRequestTemplate(evalSettingID: number): Promise<Blob> {
    return this.downloadFile(`/evalSettings/${evalSettingID}/requestTemplate`, {});
  }

  /**
   * 評価リクエスト インポート
   * @param params エクセルフォームデータ
   * @param evalSettingID
   */
  sendRequesetExcelFile(params: FormData, evalSettingID: number): Promise<ImportRequestResults> {
    return this.postFile(ImportRequestResults, `/evalSettings/${evalSettingID}/requestTemplate`, params);
  }

  /**
   * フィードバックレポートステータス取得
   * @param evalSettingID
   */
  getReportStatus(evalSettingID: number): Promise<ReportStatus> {
    return this.get(ReportStatus, `/evalSettings/${evalSettingID}/reports/status`, {});
  }

  /**
   * フィードバックレポートダウンロード
   * @param evalSettingID
   */
  getZipReportUrl(evalSettingID: number): Promise<ReportZipUrl> {
    return this.get(ReportZipUrl, `/evalSettings/${evalSettingID}/reports`, {});
  }

  /**
   * フィードバックレポートダウンロード予約リクエスト
   * @param evalSettingID
   */
  requestZipReportDownload(evalSettingID: number): Promise<Empty> {
    return this.post(Empty, `/evalSettings/${evalSettingID}/reports`, {});
  }

  /**
   * フィードバックレポート強制生成
   * @param evalSettingID
   * @returns
   */
  forceGenerateReport(evalSettingID: number): Promise<Empty> {
    return this.post(Empty, `/evalSettings/${evalSettingID}/forceGenerateReport`, {});
  }

  /**
   * 受検者割り当て情報更新
   * @param evalSettingID
   */
  updateExamineeStudents(evalSettingID: number): Promise<UpdateExamineeStudentsResult> {
    return this.put(UpdateExamineeStudentsResult, `/evalSettings/${evalSettingID}/examinee`, {});
  }

  /**
   * 受検者進捗取得
   * @param evalSettingID
   */
  fetchExamineeStudentProgresses(evalSettingID: number): Promise<ExamineeStudentProgress[]> {
    return this.getList(ExamineeStudentProgress, `/evalSettings/${evalSettingID}/evalProgress`, {});
  }

  /**
   * 評価者追加の候補となる生徒取得
   * @param request
   */
  fetchEvaluatorCandidateStudents(request: EvaluatorCandidateStudentRequest): Promise<EvaluatorCandidateStudent[]> {
    return this.getList(EvaluatorCandidateStudent, `/evalSettings/${request.evalSettingID}/evaluatorCandidateStudents`, {
      examineeStudentID: request.examineeStudentID,
      evaluatorScopeType: request.evaluatorScopeType,
    });
  }

  /**
   * 自動生成評価リストダウンロード
   * @param evalSettingID
   * @param targetDomainType ランダム対象となる範囲
   */
  downloadRandomEvalRequests(evalSettingID: number, targetDomainType: GEnum.TargetDomainType, isPriorityTag: boolean, tagID: number): Promise<Blob> {
    return this.downloadFile(`/evalSettings/${evalSettingID}/template/autoGeneratedEvalPairs`, { targetDomainType, isPriorityTag, tagID });
  }

  /**
   * 登録済み評価リストダウンロード
   * @param evalSettingID
   */
  downloadRegisteredEvalRequests(evalSettingID: number): Promise<Blob> {
    return this.downloadFile(`/evalSettings/${evalSettingID}/template/registeredEvalPairs`, {});
  }

  /**
   * 評価リクエスト追加
   * @param evalSettingID
   */
  addEvalRequest(addEvalRequest: AddEvalRequest): Promise<Empty> {
    return this.post(Empty, `/evalSettings/${addEvalRequest.evalSettingID}/evalRequest`, {
      examineeStudentID: addEvalRequest.examineeStudentID,
      evaluatorStudentID: addEvalRequest.evaluatorStudentID,
    });
  }

  /**
   * 評価リクエスト削除
   * @param evalSettingID
   */
  deleteEvalRequest(deleteEvalRequest: DeleteEvalRequest): Promise<Empty> {
    return this.delete(
      `/evalSettings/${deleteEvalRequest.evalSettingID}/evalRequest?examineeStudentID=${deleteEvalRequest.examineeStudentID}&evaluatorStudentID=${deleteEvalRequest.evaluatorStudentID}`,
      {}
    );
  }

  toggleMailSetting(evalSettingID: number): Promise<EvalSettingMailToggleResponse> {
    return this.put(EvalSettingMailToggleResponse, `/evalSettings/${evalSettingID}/mailSetting`, {});
  }
}

export const evalSettingService = new EvalSettingService();
